<script>
import { validationMixin } from 'vuelidate'
import { required, maxLength } from 'vuelidate/lib/validators'
import { rules } from '@/utils/validators'

import UploadImage from '@/components/UploadImage'
import GoogleAddressAutocomplete from '@/components/GoogleAddressAutocomplete'
import googleMapsApi from '@/mixins/googleMapsApi'

export default {
  name: 'OnboardingProfileSetup',
  components: { UploadImage },
  mixins: [validationMixin],
  props: {
    user: { type: Object, required: true },
    submitting: { type: Boolean, default: () => false }
  },
  data() {
    return {
      rules,
      form: {
        avatar: null,
        description: '',
        agreements: {
          halal: false
        }
      },

      uploadedFile: null
    }
  },
  created() {
    // Event tracking
    const { once } = this.lodash
    const { logEvent } = this.$amplitude

    this.avatarAdded = once(() => {
      logEvent('Seller Onboarding: Avatar Added')
    })

    const descriptionAdded = once(() => {
      const props = { description: this.form.description }
      logEvent('Seller Onboarding: Description Added')
    })
    this.descriptionAdded = () => {
      if (this.form.description) descriptionAdded()
    }

    this.halalAgreed = once(() => {
      logEvent('Seller Onboarding: Halal Agreed')
    })

    // Prefill form
    const { avatar, description, agreements } = this.user
    const { halal } = agreements || {}

    this.form = {
      avatar,
      description,
      agreements: { halal }
    }
  },
  methods: {
    onFileChange({ src, file }) {
      this.uploadedFile = file
      this.form.avatar = src

      this.avatarAdded()
    },

    submit() {
      if (this.submitting || this.$v.$invalid) return

      const data = {
        ...this.form,
        avatar: this.uploadedFile
      }
      this.$emit('submit', data)
    }
  },
  validations: {
    form: {
      avatar: { required },
      description: {
        required,
        maxLength: maxLength(180)
      }
    }
  }
}
</script>

<template>
  <div class="view-grid">
    <div>
      <!-- Onboarding Profile // Setup -->
      <form @submit.prevent="submit()">
        <!-- Title -->
        <div class="display-1 font-weight-bold accent--text">
          Fill in your details
        </div>

        <!-- Photo and Title -->
        <div class="my-7">
          <v-layout>
            <v-flex xs2 mr-3>
              <UploadImage
                :default-src="form.avatar"
                rounded
                @input="onFileChange"
              />
            </v-flex>

            <!-- Chef Name -->
            <v-flex xs3>
              <div class="body-1 font-weight-bold">
                {{ user.firstName }}
              </div>
            </v-flex>

            <div class="d-flex align-center">
              <div class="secondary--text caption text-right">
                Best profile photos convey authenticity and trust.
              </div>
            </div>
          </v-layout>
        </div>

        <!-- Story -->
        <div class="my-3">
          <div class="font-weight-bold">
            What's your story?
          </div>
          <div class="body-2">
            How do you think about food? Where did you learn your recipes?
          </div>

          <v-text-field
            v-model.trim="form.description"
            :rules="[rules.required]"
            class="secondary--text"
            label="Tell us about yourself..."
            counter="180"
            @blur="descriptionAdded"
          />
        </div>

        <!-- Halal friendly (Required) -->
        <div class="my-3">
          <v-checkbox
            v-model="form.agreements.halal"
            color="primary"
            class="ma-0 pa-0"
            @change="halalAgreed"
          >
            <template v-slot:label>
              <span class="body-2">
                My kitchen is halal friendly
              </span>
            </template>
          </v-checkbox>
        </div>

        <!-- Action Buttons -->
        <div class="my-5">
          <v-btn
            :loading="submitting"
            :disabled="$v.$invalid"
            type="submit"
            rounded
            depressed
            color="primary"
          >
            Continue
          </v-btn>
        </div>
      </form>
    </div>
  </div>
</template>
